import ContentService from 'services/loader-handler-services/content-service';
import CustomPageService from 'services/course-handler-services/custom-page-service';
import LocaleService from 'services/loader-handler-services/locale-service';
import Utils from 'lib/utils';
import SurveyService from 'services/data-handler-services/survey-service';
import EvaluationService from 'services/data-handler-services/evaluation-service';
import AssessmentService from 'services/data-handler-services/assessment-service';
import CourseSettingsService from 'services/course-handler-services/course-settings-service';
import LearnerProfileService from 'services/course-handler-services/learner-profile-service';
import EnrollmentDataService from 'services/data-handler-services/enrollment-data-service';
import EF from 'ef/ef';
import $ from 'jquery';

const PageService = {
  loadPage: function (moduleKey, activityKey, pageKey) {
    const pagePresent = pageKey !== undefined && pageKey !== null && pageKey !== '';
    const activityPresent = activityKey !== undefined && activityKey !== null && activityKey !== '';

    if (!EF.module || moduleKey !== EF.module.get('key')) {
      EF.setModuleByKey(moduleKey);
      LocaleService.setModuleResources(moduleKey);
    }

    if (!EF.module?.currentActivity || activityKey !== EF.module.currentActivity.get('key')) {
      if (activityPresent) {
        EF.module.setActivityByKey(activityKey);
      } else {
        EF.module.getStartingActivity();
      }
      if (!EF.module.currentActivity) {
        EF.module.currentActivity = EF.module.activities.head();
      }
      if (EF.module.currentActivity.isComplete()) {
        EF.module.currentActivity.set('redo', true);
      }
    }

    if (!EF.module.currentActivity.currentPage || pageKey !== EF.module.currentActivity.currentPage.get('key')) {
      if (pagePresent) {
        EF.module.currentActivity.setPageByKey(pageKey);
      } else {
        EF.module.currentActivity.getStartingPage();
      }
      if (!EF.module.currentActivity.currentPage) {
        EF.module.currentActivity.currentPage = EF.module.currentActivity.pages.head();
      }
    }

    ContentService.addForgePathsToContentRegion('.before-content', ContentService.moduleContent.modules);
    return ContentService.applyPageContent(EF.module.currentActivity.currentPage);
  },
  getNextPage: function () {
    return EF.module.getNextPageByKey(EF.module.currentActivity.currentPage.get('key'));
  },

  getPageByKey: function (key) {
    let activity = {};
    if (key.indexOf('/') > -1) {
      const split = key.split('/');
      if (split.length > 2) {
        const moduleName = split[0];
        const module = EF.modules.find(m => {
          return m.get('key') === moduleName;
        });
        const activityName = split[1];
        activity = module.activities.find(acc => {
          return acc.get('key') === activityName;
        });
        key = split[2];
      } else {
        const activityName = split[0];
        activity = EF.module.activities.find(acc => {
          return acc.get('key') === activityName;
        });
        key = split[1];
      }
    } else {
      activity = EF.module.currentActivity;
    }
    return activity.pages.find(function (page) {
      return page.get('key') === key;
    });
  },

  getPreviousPage: function () {
    return EF.module.getPreviousPageByKey(EF.module.currentActivity.currentPage.get('key'));
  },

  shouldPreventPageSkipping: function () {
    return CourseSettingsService.get('preventPageSkipping') !== undefined
      ? Boolean(CourseSettingsService.get('preventPageSkipping'))
      : true;
  },

  preventSkippingPages: function (page, params) {
    const localSkipping = EF.options.local ? EF.options.pageSkipping : false;
    if (
      params.page &&
      !localSkipping &&
      !EF.enrollment.get('demoMode') &&
      !EF.module.isComplete() &&
      this.shouldPreventPageSkipping()
    ) {
      // prevent skipping incomplete pages
      const lastCompleted = EF.module.currentActivity.pages.lastCompleted();
      const key = lastCompleted.get('key');
      let next = EF.module.currentActivity.nextPageByKey(key);
      while (next && next.get('optional')) {
        next = EF.module.currentActivity.nextPageByKey(next.get('key'));
      }
      if (next && page.get('index') > next.get('index')) {
        Utils.redirectToPage(lastCompleted);
        return false;
      }
    }
    return true;
  },
  shouldSkipLearner: function (skipJSON) {
    // Allow for specifying page-skipping based on parameters in the learner_profile
    // or the enrollment data.  Enrollment data accepts dot-string keys.
    //
    // Usage Example:
    // # Hide the page for Corporate users AND users in either Alaska OR California
    // skip:
    //   learner_profile:
    //     - key: learner_location_iso_code
    //       show:
    //         - US-AK
    //         - US-CA
    //     - key: org_type
    //       hide:
    //         - CO
    //
    // # Show the page for users who chose the "APAC" region on a form set up
    // # using a custom set of regions.
    // skip:
    //   learner_profile:
    //     - key: customLearnerProfile.region
    //       show:
    //         - APAC
    const findMatches = (arr, value, fallback) => {
      if (arr && Array.isArray(arr)) {
        return arr.map(criterion => value === criterion).some(i => i);
      } else {
        return fallback;
      }
    };

    const checkHideShow = (hide, show, value) => {
      const hidden = findMatches(hide, value, false);
      const shown = findMatches(show, value, true);
      return hidden || !shown;
    };

    if (EF.forgeMode) {
      return false; // Skip is not applicable in forge mode
    }

    let skipLearnerProfile = false;
    let skipEnrollmentData = false;
    if (skipJSON.learner_profile) {
      skipLearnerProfile = skipJSON.learner_profile
        .map(({ hide, show, key }) => checkHideShow(hide, show, LearnerProfileService.get(key)))
        .find(i => i);
    }

    if (skipJSON.enrollment_data) {
      skipEnrollmentData = skipJSON.enrollment_data
        .map(({ hide, show, key }) => {
          return checkHideShow(hide, show, EnrollmentDataService.getByDotString(key));
        })
        .find(i => i);
    }
    return skipLearnerProfile || skipEnrollmentData;
  },
  shouldBeSkipped: async function (page) {
    const skipCallback = page.script.skip;
    const skipJSON = page.model.get('skip');

    const evaluationShouldBeSkipped = EvaluationService.shouldSkip();

    if (EF.forgeMode) {
      return false;
    }
    if (skipCallback) {
      return skipCallback(page);
    } else if (skipJSON) {
      let customizationAbsent;
      if (skipJSON.customization) {
        const { module, activity, page } = CustomPageService.modulePaths();
        customizationAbsent =
          !CustomPageService.hasPageCustomization(skipJSON.slug, module, activity, page) &&
          EF.customizations.getByKeyAndSlug(skipJSON.customization, skipJSON.slug) === undefined;
      } else if (skipJSON.resource_type) {
        customizationAbsent = EF.resources.byTypeSync(skipJSON.resource_type, skipJSON.resource_sub_type).length === 0;
      } else if (skipJSON.survey_slug) {
        customizationAbsent =
          (await SurveyService.userHasCompletedSurvey(skipJSON.survey_slug)) || evaluationShouldBeSkipped;
      } else if (skipJSON.assessment_slug) {
        customizationAbsent =
          (await AssessmentService.userHasCompletedAssessment(skipJSON.assessment_slug)) || evaluationShouldBeSkipped;
      } else if (skipJSON.learner_profile || skipJSON.enrollment_data) {
        customizationAbsent = this.shouldSkipLearner(skipJSON);
      }
      return skipJSON.when === 'present' ? !customizationAbsent : customizationAbsent;
    }
  },
  setPageOptional: function (page) {
    const currentPage = EF.module.currentActivity.currentPage;

    EF.module.currentActivity.pages.each(p => {
      if (p.get('index') >= currentPage.get('index') && p.get('index') <= page.get('index')) {
        page.set('optional', true);
      }
    });
  },
  setScrollToElement: function ($element, $scrollPane, scroll = 'y') {
    if ($element && $scrollPane) {
      setTimeout(() => {
        // As of now it only supports 'vertical scroll',
        if (scroll === 'y') {
          $scrollPane.animate(
            {
              scrollTop: this.getElementScrollPos($element, $scrollPane),
            },
            500
          );
        }
      }, 500);
    }
  },
  getElementScrollPos: function ($element, $scrollPane, scroll = 'y') {
    let scrollPos = 0;

    if ($element && $scrollPane) {
      const $siteContainer = $('#site-container');
      // As of now it only returns 'vertical scroll position' for '$element',
      if (scroll === 'y') {
        const $siteContainerPosY =
          $siteContainer.offset().top - parseInt($siteContainer.css('margin-top').replace('px', ''));
        const scrollPaneBoundingClientRectTop =
          $scrollPane.selector === 'html, body' ? $siteContainerPosY : $scrollPane.get(0).getBoundingClientRect().top;
        const scrollPaneScrollTop = $scrollPane.scrollTop();
        const elementBoundingClientRectTop = $element.get(0).getBoundingClientRect().top;

        scrollPos = elementBoundingClientRectTop - scrollPaneBoundingClientRectTop + scrollPaneScrollTop;
      }
    }

    return scrollPos;
  },
};

export default PageService;
